import Link from 'next/link';
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ComponentProps, ReactNode, Ref } from 'react';
import { cn } from '@/lib/utils';

/**
 * The one shared button. Every call-to-action in the app should render
 * through this component so a future brand pass only restyles this file.
 *
 * Deliberately restrained: flat brand navy (the `cta` tokens in
 * app/globals.css), no gradients, no hover scale, no heavy shadows.
 *
 * Renders a Next <Link> when `href` is given, otherwise a <button>
 * (type="button" unless you pass another type).
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const base =
  'inline-flex items-center justify-center gap-2 rounded-lg border font-semibold text-center transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50';

const variants: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-cta text-white hover:bg-cta-hover disabled:hover:bg-cta',
  secondary: 'border-cta bg-surface text-cta hover:bg-cta-secondary disabled:hover:bg-surface',
  ghost: 'border-transparent bg-transparent text-cta hover:bg-cta-secondary disabled:hover:bg-transparent',
  link: 'border-transparent bg-transparent text-cta underline-offset-4 hover:underline',
};

// Padding-based so a button can hold two lines (title + caption) and
// still size itself. `link` ignores these and stays inline with text.
const sizes: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-7 py-3.5 text-base',
  xl: 'px-7 py-4 text-lg',
};

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to the container width. */
  fullWidth?: boolean;
  className?: string;
  children?: ReactNode;
}

export type ButtonAsLinkProps = CommonProps &
  Omit<ComponentProps<typeof Link>, 'className' | 'children'> & {
    href: ComponentProps<typeof Link>['href'];
    /** Links cannot be disabled natively; a disabled link renders as an inert, aria-disabled span. */
    disabled?: boolean;
  };

export type ButtonAsButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    href?: undefined;
  };

export type ButtonProps = ButtonAsLinkProps | ButtonAsButtonProps;

export function buttonClasses({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: Pick<CommonProps, 'variant' | 'size' | 'fullWidth' | 'className'> = {}) {
  return cn(
    base,
    variants[variant],
    variant === 'link' ? 'p-0 text-[length:inherit]' : sizes[size],
    fullWidth && 'w-full',
    className,
  );
}

const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement | HTMLSpanElement, ButtonProps>(function Button(props, ref) {
  if (props.href !== undefined) {
    const { variant, size, fullWidth, className, children, disabled, ...rest } = props as ButtonAsLinkProps;
    const classes = buttonClasses({ variant, size, fullWidth, className });
    // A disabled link renders as inert text-in-a-button-shape rather than an
    // <a> with a click-blocking handler, so this stays usable from server
    // components (functions cannot cross into the client <Link>).
    if (disabled) {
      return (
        <span ref={ref as Ref<HTMLSpanElement>} role="link" aria-disabled="true" className={classes}>
          {children}
        </span>
      );
    }
    return (
      <Link {...rest} ref={ref as Ref<HTMLAnchorElement>} className={classes}>
        {children}
      </Link>
    );
  }

  const { variant, size, fullWidth, className, children, type, ...rest } = props as ButtonAsButtonProps;
  return (
    <button
      {...rest}
      ref={ref as Ref<HTMLButtonElement>}
      type={type ?? 'button'}
      className={buttonClasses({ variant, size, fullWidth, className })}
    >
      {children}
    </button>
  );
});

export default Button;
